/**
 * Spend Easy - Shared Bills Calendar & Due-Date Reminders Utility
 * Computes calendar grids, recurring bill occurrences, overdue detection,
 * and due-date alerts across Web and Mobile platforms.
 */

import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isToday,
  isBefore,
  format,
  differenceInDays,
  addMonths,
  subMonths,
} from 'date-fns';

/**
 * Generates an array of calendar days for a 7-column monthly grid (Sunday to Saturday).
 * Includes padding days from previous and next months.
 */
export function getCalendarGridDays(targetDate = new Date()) {
  const monthStart = startOfMonth(targetDate);
  const monthEnd = endOfMonth(targetDate);
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 0 }); // Sunday
  const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 0 }); // Saturday

  const days = eachDayOfInterval({ start: gridStart, end: gridEnd });
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return days.map((day) => {
    const dayStart = new Date(day);
    dayStart.setHours(0, 0, 0, 0);

    return {
      date: day,
      dayNumber: day.getDate(),
      dateString: format(day, 'yyyy-MM-dd'),
      isCurrentMonth: isSameMonth(day, targetDate),
      isToday: isToday(day),
      isPast: isBefore(dayStart, today),
    };
  });
}

/**
 * Normalizes a bill due date to a clean Date object at 00:00:00
 */
export function normalizeDate(dateVal) {
  if (!dateVal) return new Date();
  const d = dateVal instanceof Date ? new Date(dateVal) : new Date(dateVal);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Determines whether a bill is due on a specific target calendar day,
 * taking recurring schedules (monthly, yearly, weekly) into account.
 */
export function isBillDueOnDay(bill, dayDate) {
  if (!bill || !bill.dueDate) return false;
  const billDue = normalizeDate(bill.dueDate);
  const target = normalizeDate(dayDate);

  // Exact date match
  if (
    billDue.getFullYear() === target.getFullYear() &&
    billDue.getMonth() === target.getMonth() &&
    billDue.getDate() === target.getDate()
  ) {
    return true;
  }

  // Monthly recurring match
  if (bill.repeat === 'monthly') {
    // If the day of month matches
    const targetDayOfMonth = target.getDate();
    const billDueDay = billDue.getDate();
    // Handle months with fewer days (e.g. 31st on a 30-day month)
    const daysInTargetMonth = endOfMonth(target).getDate();
    const effectiveBillDay = Math.min(billDueDay, daysInTargetMonth);

    return targetDayOfMonth === effectiveBillDay && target >= billDue;
  }

  // Yearly recurring match
  if (bill.repeat === 'yearly') {
    return (
      billDue.getMonth() === target.getMonth() &&
      billDue.getDate() === target.getDate() &&
      target >= billDue
    );
  }

  // Weekly recurring match
  if (bill.repeat === 'weekly') {
    return billDue.getDay() === target.getDay() && target >= billDue;
  }

  return false;
}

/**
 * Returns all bills due on a given day.
 */
export function getBillsForDay(bills, dayDate) {
  if (!Array.isArray(bills)) return [];
  return bills.filter((b) => isBillDueOnDay(b, dayDate));
}

/**
 * Calculates due status for a bill: 'overdue' | 'due-today' | 'due-soon' | 'upcoming' | 'paid'
 */
export function getBillDueStatus(bill, targetDate = new Date()) {
  if (!bill) return 'upcoming';
  if (bill.isPaid) return 'paid';

  const today = normalizeDate(targetDate);
  const billDue = normalizeDate(bill.dueDate);
  const daysDiff = differenceInDays(billDue, today);

  if (daysDiff < 0) return 'overdue';
  if (daysDiff === 0) return 'due-today';
  if (daysDiff > 0 && daysDiff <= 3) return 'due-soon';
  return 'upcoming';
}

/**
 * Visual metadata for bill status pills & badges
 */
export const BILL_STATUS_META = {
  'overdue': {
    label: 'Overdue',
    color: '#EF4444',
    bg: '#FEE2E2',
    textDark: '#DC2626',
    icon: 'alert-circle',
    badgeClass: 'bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-300 border-red-200 dark:border-red-800',
  },
  'due-today': {
    label: 'Due Today',
    color: '#F97316',
    bg: '#FFEDD5',
    textDark: '#EA580C',
    icon: 'time',
    badgeClass: 'bg-orange-100 text-orange-700 dark:bg-orange-950/50 dark:text-orange-300 border-orange-200 dark:border-orange-800',
  },
  'due-soon': {
    label: 'Due Soon',
    color: '#F59E0B',
    bg: '#FEF3C7',
    textDark: '#D97706',
    icon: 'hourglass',
    badgeClass: 'bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300 border-amber-200 dark:border-amber-800',
  },
  'upcoming': {
    label: 'Upcoming',
    color: '#3B82F6',
    bg: '#DBEAFE',
    textDark: '#2563EB',
    icon: 'calendar',
    badgeClass: 'bg-blue-100 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300 border-blue-200 dark:border-blue-800',
  },
  'paid': {
    label: 'Paid',
    color: '#10B981',
    bg: '#D1FAE5',
    textDark: '#059669',
    icon: 'checkmark-circle',
    badgeClass: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800',
  },
};

/**
 * Calculates high-level monthly bills summary metrics and urgent reminders
 */
export function calculateBillsCalendarSummary(bills = [], targetDate = new Date()) {
  const today = normalizeDate(new Date());
  const monthStart = startOfMonth(targetDate);
  const monthEnd = endOfMonth(targetDate);

  let totalDue = 0;
  let totalPaid = 0;
  const overdueBills = [];
  const dueTodayBills = [];
  const dueSoonBills = [];
  const upcomingBills = [];
  const paidBills = [];

  bills.forEach((bill) => {
    const amount = Number(bill.amount) || 0;
    const billDue = normalizeDate(bill.dueDate);
    const status = getBillDueStatus(bill, today);

    if (bill.isPaid) {
      totalPaid += amount;
      paidBills.push(bill);
    } else {
      totalDue += amount;
      if (status === 'overdue') overdueBills.push(bill);
      else if (status === 'due-today') dueTodayBills.push(bill);
      else if (status === 'due-soon') dueSoonBills.push(bill);
      else upcomingBills.push(bill);
    }
  });

  // Urgent list: Overdue + Due Today + Due in next 3 days
  const urgentReminders = [...overdueBills, ...dueTodayBills, ...dueSoonBills];

  return {
    totalDue,
    totalPaid,
    totalBillsAmount: totalDue + totalPaid,
    unpaidCount: overdueBills.length + dueTodayBills.length + dueSoonBills.length + upcomingBills.length,
    paidCount: paidBills.length,
    overdueBills,
    dueTodayBills,
    dueSoonBills,
    upcomingBills,
    paidBills,
    urgentReminders,
    hasUrgentBills: urgentReminders.length > 0,
  };
}
